/**
 * The Forma stage card — Pomiary's „Dwie daty" stage over a movement's sessions: the current
 * reading, one sentence about A to B, the Linia switch, the plot with its draggable A and B, the
 * recorded sessions as marked points, and the Trend and Prognoza toggles. No time presets (canon).
 * The hand-over sits on the say block and the plot box, which stand across every exercise switch.
 */

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { useTranslation } from 'react-i18next';


import type { LiftLine } from '~/entities/lift-history';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ReadoutChart } from '~/shared/ui/chart/ReadoutChart';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { HintPopover } from '~/shared/ui/HintPopover';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { FormaView } from '../model/formaView';
import type { MarkerKey, SelectionMove } from '../model/twoDates';

import { useCardAnnounce } from './cardAnnouncer';
import styles from './DetailTab.module.css';
import forma from './Forma.module.css';
import { RecordedPoints } from './RecordedPoints';
import {
  twoDatesPlot,
  TwoDatesPlotLayers,
  TwoDatesSayWait,
  TwoDatesSentenceText,
  TwoDatesToggles,
  type ChartAid,
} from './TwoDatesStageParts';

interface FormaStageProps {
  readonly view: FormaView;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly onLine: (line: LiftLine) => void;
  readonly trend: boolean;
  readonly projection: boolean;
  readonly onToggle: (which: ChartAid) => void;
  readonly onMove: (move: SelectionMove) => void;
  readonly onShowRecorded: (clipId: number) => void;
  readonly reducedMotion: boolean;
}

export function FormaStage({
  view,
  gate,
  failed,
  onRetry,
  onLine,
  trend,
  projection,
  onToggle,
  onMove,
  onShowRecorded,
  reducedMotion,
}: FormaStageProps) {
  const { t } = useTranslation();
  const announce = useCardAnnounce();
  const nameOf = (marker: MarkerKey) => t(`web:forma.stage.marker.${marker}`);
  const answered = !gate.waiting && !failed;
  // Through the wait the switch shows what the fields promise, so a switch between two lifts keeps it.
  const lines = gate.waiting ? view.promised.lines : failed ? [] : view.lines;
  const chosen = gate.waiting ? view.promised.line : view.line;
  const only = lines.length === 1 ? lines[0] : undefined;
  // A switch that stood through the wait is already on screen and must not enter again.
  const lineReveal = view.promised.lines.length > 0 ? '' : gate.reveal;

  return (
    <section className={`${String(styles.card)} ${String(styles.stageCard)}`}>
      <div className={`${String(forma.stageSay)} ${gate.reveal}`}>
        {gate.waiting ? (
          <TwoDatesSayWait skeleton={gate.skeleton} />
        ) : failed || view.empty ? null : (
          <>
            {view.current === null ? null : (
              <p className={styles.nowLine}>
                <span className={styles.nowLabel}>{view.current.label}</span>
                <b className={styles.nowValue}>{view.current.value}</b>
                <span className={styles.nowDate}>{view.current.dateLabel}</span>
              </p>
            )}
            {view.sentence === null ? (
              <p className={styles.sentenceQuiet}>
                {view.noSeries ? t('web:forma.stage.noSeries') : t('web:forma.stage.needsTwo')}
              </p>
            ) : (
              <TwoDatesSentenceText sentence={view.sentence} />
            )}
          </>
        )}
      </div>

      {/* The movement's own lines; which lines there are is part of the answer, so the row holds its
        * height whatever stands in it. One line is named, not offered as a choice. */}
      <div className={forma.lineRow}>
        <span className={styles.lineLabel}>{t('web:clientDetail.body.lineLabel')}</span>
        {lines.length === 0 ? null : (
          <>
            {only === undefined ? (
              <RadioGroup
                aria-label={t('web:forma.line.switch')}
                className={`${String(styles.seg)} ${lineReveal}`}
                onValueChange={(next) => {
                  const picked = lines.find((option) => option.key === next);
                  if (picked !== undefined) onLine(picked.key);
                }}
                value={chosen}
              >
                {lines.map((option) => (
                  <Radio.Root className={styles.segOption} key={option.key} value={option.key}>
                    {option.label}
                  </Radio.Root>
                ))}
              </RadioGroup>
            ) : (
              <span className={`${String(forma.lineOnly)} ${lineReveal}`}>{only.label}</span>
            )}
            <HintPopover
              glyph="question"
              label={t('web:forma.line.help.label')}
              terms={lines.map((option) => ({ term: option.label, meaning: option.meaning }))}
            >
              {t('web:forma.line.help.footnote')}
            </HintPopover>
          </>
        )}
      </div>

      <div aria-busy={gate.waiting} className={`${String(forma.plot)} ${gate.reveal}`}>
        {gate.waiting ? (
          <div className={forma.plotReserve}>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelPlot)}`} /> : null}
          </div>
        ) : failed ? (
          <div className={forma.plotReserve}>
            <EmptyState
              mark={<DumbbellMarkIcon />}
              title={
                <span>
                  {t('web:forma.stage.failed')}{' '}
                  <button className={inlineButton.inlineTextButton} onClick={onRetry} type="button">
                    {t('web:clientDetail.states.retry')}
                  </button>
                </span>
              }
              tone="neutral"
            />
          </div>
        ) : view.readings.length === 0 ? (
          <div className={forma.plotReserve}>
            <EmptyState
              mark={<DumbbellMarkIcon />}
              title={view.noSeries ? t('web:forma.stage.noSeries') : t('web:forma.stage.empty')}
              tone="neutral"
            />
          </div>
        ) : (
          <ReadoutChart
            colour="var(--chart-series-neutral)"
            better={view.better}
            extraYValues={view.extraY}
            {...(view.chartFigure === null ? {} : { figure: view.chartFigure })}
            height="var(--h-chart-plot-forma)"
            name={view.lineLabel}
            plot={twoDatesPlot(view.projection !== null)}
            points={view.readings}
            reducedMotion={reducedMotion}
            seriesAs={view.trend === null ? 'line' : 'dots'}
            unit={view.unit}
            zeroBaseline={false}
            {...(view.xDomain === null ? {} : { xDomain: view.xDomain })}
          >
            <TwoDatesPlotLayers
              nameOf={nameOf}
              onMove={onMove}
              reducedMotion={reducedMotion}
              view={view}
            />
            <RecordedPoints
              onShowInPanel={(point) => {
                onShowRecorded(point.clipId);
                announce(t('web:forma.panel.shown', { date: point.dateLong }));
              }}
              points={view.recorded}
            />
          </ReadoutChart>
        )}
      </div>

      <TwoDatesToggles
        answered={answered}
        instruction={t('web:forma.stage.instruction')}
        layout="compact"
        refusals={{
          trend: t('web:forma.stage.trendUnavailable'),
          projection: t('web:forma.stage.projectionUnavailable'),
        }}
        onToggle={onToggle}
        projection={projection}
        projectionAvailable={view.projectionAvailable}
        trend={trend}
        trendAvailable={view.trendAvailable}
      />
    </section>
  );
}
